import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Quote, Star, ExternalLink } from "lucide-react";
interface TestimonialData {
  id: number;
  author: string;
  source: string;
  rating: number;
  excerpt?: string;
  quote: string;
  googleUrl: string;
}
const testimonials: TestimonialData[] = [{
  id: 1,
  author: "Gill Thomas",
  source: "Google Review",
  rating: 5,
  excerpt: "Without a doubt the best physio I've seen! I've had really serious and debilitating musculoskeletal problems for the last few years and tried several physios, without any improvement.",
  quote: "Without a doubt the best physio I've seen! I've had really serious and debilitating musculoskeletal problems for the last few years and tried several physios, without any improvement. I was pretty much at rock bottom when I tried Align Physiotherapy. From the very first session, Anthony was welcoming, professional, and genuinely focused on helping me. He took the time to listen and thoroughly assess everything, he explained his suggested treatment plan, and answered all my questions. I've had a huge improvement since working with Anthony, it's like I'm getting my life back! I would highly recommend Align Physiotherapy to anyone.",
  googleUrl: "https://www.google.com/maps/contrib/107600175494637754303/reviews?hl=en-GB"
}, {
  id: 2,
  author: "Scott Lomax",
  source: "Google Review",
  rating: 5,
  quote: "My 1st time at Align Physiotherapy today for knee issues, Anthony was really knowledgeable and helped me with my ongoing issue, would definitely recommend him to others 5*",
  googleUrl: "https://www.google.com/maps/contrib/113658030634285674639/reviews?hl=en-GB"
}];
function TestimonialCard({
  item
}: {
  item: TestimonialData;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayText = item.excerpt && !isExpanded ? item.excerpt : item.quote;
  return <Card className="h-full border border-border shadow-soft hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)]">
      <CardContent className="p-8 flex flex-col h-full">
        <div className="flex items-center justify-between gap-4 mb-4">
          <Quote className="w-10 h-10 text-accent opacity-60 shrink-0" />
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <div className="flex items-center gap-0.5 text-amber-500" aria-label={`${item.rating} out of 5 stars`}>
              {Array.from({
              length: item.rating
            }).map((_, idx) => <Star key={idx} className="size-4 fill-amber-500 text-amber-500" />)}
            </div>
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-secondary text-xs font-medium text-foreground border border-border">
              <span className="font-semibold text-primary mr-1">Google</span> Review
            </span>
          </div>
        </div>

        <blockquote className="flex-grow mb-6">
          <p className="text-foreground text-base md:text-lg leading-[1.6] font-sans">
            "{displayText}"
          </p>
          {item.excerpt && <div className="mt-2">
              <Button variant="link" size="sm" onClick={() => setIsExpanded(!isExpanded)} className="px-0 h-auto text-primary hover:text-primary-hover font-semibold text-sm underline underline-offset-2">
                {isExpanded ? "Show less" : "Read full review"}
              </Button>
            </div>}
        </blockquote>

        <div className="border-t border-border pt-4 flex items-center justify-between gap-4 mt-auto">
          <div>
            <p className="font-display font-semibold text-foreground text-lg">
              {item.author}
            </p>
            <p className="text-muted-foreground text-sm mt-0.5">
              Verified Patient
            </p>
          </div>
          <Link to={item.googleUrl} newTab rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary-hover underline underline-offset-4 shrink-0 transition-colors">
            View on Google
            <ExternalLink className="size-3.5" />
          </Link>
        </div>
      </CardContent>
    </Card>;
}
export default function Testimonials() {
  return <section className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-wide">
            What Our Patients Say
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-[1.6]">
            Real feedback from real people who have experienced our evidence-based physiotherapy care.
          </p>
        </div>

        <div className="max-w-5xl mx-auto animate-fade-in-up-delay-100">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/2">
                  <TestimonialCard item={testimonial} />
                </CarouselItem>)}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-12 bg-primary text-primary-foreground hover:bg-primary-hover border-0 shadow-medium" />
            <CarouselNext className="hidden md:flex -right-12 bg-primary text-primary-foreground hover:bg-primary-hover border-0 shadow-medium" />
          </Carousel>

          <div className="mt-12 text-center flex flex-col items-center justify-center gap-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-amber-500" aria-label="5 out of 5 stars">
                {Array.from({
                length: 5
              }).map((_, idx) => <Star key={idx} className="size-5 fill-amber-500 text-amber-500" />)}
              </div>
              <span className="font-display font-semibold text-foreground text-base md:text-lg">
                5.0 on Google
              </span>
            </div>
            <Link to="https://share.google/ZDbeiZGcEssAsXAKZ" newTab rel="noopener noreferrer" className="text-primary hover:text-primary-hover font-medium text-sm md:text-base underline underline-offset-4 transition-colors">
              Read all our Google reviews
            </Link>
          </div>
        </div>
      </div>
    </section>;
}